'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface SessionReviewModalProps {
  bookingId: string;
  consultantName: string;
  consultantInitials: string;
  serviceName: string;
  sessionDate: string;
  revieweeId: string;
  reviewerId: string;
  onClose: () => void;
  onSubmitted?: () => void;
}

// ─── Star Rating ──────────────────────────────────────────────────────────────
function StarRating({
  value,
  onChange,
  readonly = false,
}: {
  value: number;
  onChange?: (v: number) => void;
  readonly?: boolean;
}) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hovered || value) >= star;
        return (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange?.(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            className={`w-9 h-9 transition-all duration-100 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          >
            <svg viewBox="0 0 20 20" fill={filled ? '#f59e0b' : 'none'} stroke={filled ? '#f59e0b' : '#d1d5db'} strokeWidth="1.5">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

// ─── Modal ────────────────────────────────────────────────────────────────────
export default function SessionReviewModal({
  bookingId,
  consultantName,
  consultantInitials,
  serviceName,
  sessionDate,
  revieweeId,
  reviewerId,
  onClose,
  onSubmitted,
}: SessionReviewModalProps) {
  const supabase = createClient();

  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a star rating before submitting.');
      return;
    }
    if (feedback.trim().length < 10) {
      setError('Please write at least 10 characters of feedback.');
      return;
    }

    setSubmitting(true);

    const { error: insertError } = await supabase.from('session_reviews').insert({
      booking_id: bookingId,
      reviewer_id: reviewerId,
      reviewee_id: revieweeId || reviewerId,
      reviewer_role: 'client',
      rating,
      feedback: feedback.trim(),
      is_visible_on_profile: isVisible,
    });

    setSubmitting(false);

    if (insertError) {
      if (insertError.code === '23505') {
        setError('You have already submitted a review for this session.');
      } else {
        setError(insertError.message);
      }
    } else {
      setSubmitted(true);
      onSubmitted?.();
    }
  }

  // ── Success State ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-card border border-border rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full gradient-teal flex items-center justify-center mx-auto mb-5 shadow-lg">
            <Icon name="CheckIcon" size={32} className="text-white" />
          </div>
          <h2 className="text-xl font-700 text-foreground mb-2">Review Submitted!</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5">
            Thank you for your feedback. Your review helps the community find the best consultants.
          </p>
          <div className="flex justify-center gap-1 mb-6">
            {[1, 2, 3, 4, 5].map((s) => (
              <svg key={s} className={`w-6 h-6 transition-opacity ${s <= rating ? 'opacity-100' : 'opacity-20'}`} viewBox="0 0 20 20" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <button
            onClick={onClose}
            className="w-full bg-primary text-primary-foreground font-600 py-3 rounded-xl hover:bg-primary/90 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // ── Form State ─────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl gradient-teal flex items-center justify-center">
              <Icon name="StarIcon" size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-700 text-foreground">Rate Your Session</h2>
              <p className="text-xs text-muted-foreground">Share your experience</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
            aria-label="Close"
          >
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

            {/* Consultant Info */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border border-border">
              <div className="w-10 h-10 rounded-full gradient-teal flex items-center justify-center text-white font-600 text-sm flex-shrink-0">
                {consultantInitials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-600 text-foreground truncate">{consultantName}</p>
                <p className="text-xs text-muted-foreground truncate">{serviceName} · {sessionDate}</p>
              </div>
            </div>

            {/* Star Rating */}
            <div>
              <label className="block text-sm font-600 text-foreground mb-3">
                Overall Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col items-center gap-2">
                <StarRating value={rating} onChange={setRating} />
                <div className="h-5">
                  {rating > 0 ? (
                    <span className="text-sm font-600 text-amber-600 bg-amber-50 px-3 py-0.5 rounded-full">
                      {RATING_LABELS[rating]}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Tap a star to rate</span>
                  )}
                </div>
              </div>
            </div>

            {/* Written Feedback */}
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                Written Feedback <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">Minimum 10 characters</p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Describe what went well, what could be improved, and any highlights from your session…"
                rows={4}
                maxLength={1000}
                className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none leading-relaxed"
              />
              <div className="flex items-center justify-between mt-1.5">
                <span className={`text-xs ${feedback.length > 0 && feedback.length < 10 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {feedback.length > 0 && feedback.length < 10 ? `${10 - feedback.length} more characters needed` : ''}
                </span>
                <span className="text-xs text-muted-foreground">{feedback.length}/1000</span>
              </div>
            </div>

            {/* Visibility Toggle */}
            <label className="flex items-center justify-between cursor-pointer gap-4 p-3 bg-muted/40 rounded-xl border border-border">
              <div className="flex items-center gap-2.5">
                <Icon name={isVisible ? 'EyeIcon' : 'EyeSlashIcon'} size={16} className="text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-xs font-600 text-foreground">Show on consultant&apos;s profile</p>
                  <p className="text-xs text-muted-foreground">Publicly visible to other clients</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsVisible(!isVisible)}
                className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${isVisible ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                aria-label="Toggle visibility"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${isVisible ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </label>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
                <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={15} />
                  Submit Review
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
